import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';
import type { DefaultError } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { addIf, toSafeString } from '~/utils';

import { PICKUP_SHIPPING_TYPES } from '~/constants';
import { RECEIVE } from '~/constants/errorCode';

import type {
  TypePickupData,
  TypePutReceivingRequestConfirmParams,
  TypeReceiveRequestOrderInfoFilter,
} from '~/types/receive.types';

type TypePutReceivingRequestConfirmError = DefaultError & {
  /** 등록 불가한 입고 요청 id 리스트 */
  values?: number[];
};

type TypePutReceivingRequestConfirmMutationParams = {
  ownerCode: string;
  warehouse: string;
  orderInfoFilter: TypeReceiveRequestOrderInfoFilter[];
};

/** 픽업 출고지 데이터로부터 pickupReleaseInfos 객체 생성 */
const createPickupReleaseInfo = (data: TypePickupData, isDefault: boolean) => ({
  defaultOutboundYn: isDefault,
  zipNo: data.zipCode,
  address: data.address,
  ...(data.detailAddress && { detailAddress: data.detailAddress }),
  phoneNo: data.contactNumber,
  ...(data.vehicleSize?.value && { vehicleSizeCode: data.vehicleSize.value }),
  palletQty: data.palletQuantity,
  ...(data.memo && { memo: data.memo }),
});

/** 컬리픽업 출고지 정보 생성 */
const createKurlyPickupInfos = (kurlyPickupAddress: string, pickupAddressData?: Record<string, TypePickupData>) => {
  const infos: TypePutReceivingRequestConfirmParams['orders'][number]['pickupReleaseInfos'] = [];

  if (!pickupAddressData) return infos;

  if (kurlyPickupAddress === 'default' && pickupAddressData.kurlyDefault) {
    infos.push(createPickupReleaseInfo(pickupAddressData.kurlyDefault, true));
  } else if (kurlyPickupAddress === 'etc' && pickupAddressData.kurlyEtc) {
    infos.push(createPickupReleaseInfo(pickupAddressData.kurlyEtc, false));
  }

  return infos;
};

/** 밀크런픽업 출고지 정보 생성 */
const createMilkrunPickupInfos = (
  milkrunPickupAddresses: { default: boolean; etc1: boolean; etc2: boolean },
  pickupAddressData?: Record<string, TypePickupData>
) => {
  const infos: TypePutReceivingRequestConfirmParams['orders'][number]['pickupReleaseInfos'] = [];

  if (!pickupAddressData) return infos;

  if (milkrunPickupAddresses.default && pickupAddressData.milkrunDefault) {
    infos.push(createPickupReleaseInfo(pickupAddressData.milkrunDefault, true));
  }
  if (milkrunPickupAddresses.etc1 && pickupAddressData.milkrunEtc1) {
    infos.push(createPickupReleaseInfo(pickupAddressData.milkrunEtc1, false));
  }
  if (milkrunPickupAddresses.etc2 && pickupAddressData.milkrunEtc2) {
    infos.push(createPickupReleaseInfo(pickupAddressData.milkrunEtc2, false));
  }

  return infos;
};

/** 픽업 출고지 정보 생성 */
const createPickupInfos = (
  releaseCode: string,
  kurlyPickupAddress: string,
  milkrunPickupAddresses: { default: boolean; etc1: boolean; etc2: boolean },
  pickupAddressData?: Record<string, TypePickupData>
) => {
  if (releaseCode === PICKUP_SHIPPING_TYPES.KURLY_PICKUP) {
    return createKurlyPickupInfos(kurlyPickupAddress, pickupAddressData);
  }

  if (releaseCode === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP) {
    return createMilkrunPickupInfos(milkrunPickupAddresses, pickupAddressData);
  }

  return [];
};

/** API 요청 파라미터 생성 */
const createConfirmParams = ({
  warehouse,
  orderInfoFilter,
}: {
  warehouse: string;
  orderInfoFilter: TypeReceiveRequestOrderInfoFilter[];
}): TypePutReceivingRequestConfirmParams => ({
  warehouse,
  orders: orderInfoFilter.map((order) => {
    const pickupReleaseInfos = createPickupInfos(
      toSafeString(order.releaseCode?.value),
      order.kurlyPickupAddress,
      order.milkrunPickupAddresses,
      order.pickupAddressData
    );

    return {
      id: order.orderId,
      releaseCode: toSafeString(order.releaseCode?.value),
      ...addIf(!Number.isNaN(Number(order.expectedTime?.value)), 'dailyCapaId', Number(order.expectedTime?.value)),
      courier: order.courier,
      invoiceNo: order.invoiceNumber,
      ...addIf(pickupReleaseInfos.length > 0, 'pickupReleaseInfos', pickupReleaseInfos),
    };
  }),
});

const fetchPutReceivingRequestConfirm = async ({
  ownerCode,
  warehouse,
  orderInfoFilter,
}: TypePutReceivingRequestConfirmMutationParams): Promise<void> => {
  const params = createConfirmParams({ warehouse, orderInfoFilter });

  await axiosInstance.put(`/api-inbound/v1/3pl/receiving/${ownerCode}/request/confirm`, params);
};

/**
 * [입고관리 > 입고요청관리] 입고요청등록 모달 > 입고요청 확정 Mutation Hook
 */
export const usePutReceivingRequestConfirmMutation = () => {
  return useMutation<void, TypePutReceivingRequestConfirmError, TypePutReceivingRequestConfirmMutationParams>({
    mutationFn: fetchPutReceivingRequestConfirm,
    meta: { showGlobalErrorToast: false }, // 사용처에서 에러 코드별 분기 처리
  });
};

registerMockAPI<null>({
  method: 'put',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/request/confirm',
  label: '입고/입고요청/요청확정',
  scenarios: [
    { name: '정상 응답', response: null },
    {
      name: '입고일시 불가 (9030)',
      response: {
        error: {
          code: RECEIVE.RECEIVE_DATE_TIME_NOT_EXIST,
          message: '선택한 입고일시에 입고 요청이 불가능합니다. 다른 일시를 선택해 주세요.',
          values: [1001],
        },
      },
      status: 400,
    },
    {
      name: '잘못된 파라미터 (9001)',
      response: {
        error: { code: 9001, message: '입고 요청 등록에 실패했습니다.\n다시 시도해주세요.' },
      },
      status: 400,
    },
  ],
});
